/**
 * API client.
 *
 * The access token lives in a module variable, not in localStorage. That is deliberate:
 * anything reachable from `window` is reachable from an XSS payload, and the refresh
 * token — the long-lived credential — is an httpOnly cookie this code can neither read
 * nor accidentally forward. A page reload therefore starts with no access token and
 * silently re-acquires one from the cookie.
 *
 * A single in-flight refresh is shared across concurrent 401s, so a page that fires six
 * requests on mount does not trigger six token rotations.
 */
import type { ApiError, ErrorCode } from '@filmodasi/shared';

/**
 * Where the API lives.
 *
 * Empty for the web build, where the app and the API share an origin and a relative path
 * is both simpler and immune to a domain change. The packaged TV widget is the exception:
 * it runs from `file://`, so a relative path resolves to the filesystem and every request
 * fails before it reaches the network. Its build sets `VITE_API_BASE` to the operator's
 * own domain.
 *
 * Read once, at module load, from a build-time constant — there is no runtime switch to
 * get wrong.
 */
/**
 * The loader's runtime configuration, and the one place it is read.
 *
 * ## Why a runtime channel exists at all
 *
 * §29.2's loader shape: the widget is a manifest plus a local `index.html` that pulls the
 * deployed bundle off the server, so every television updates itself on the next launch
 * instead of waiting for somebody to re-sign a `.wgt` and stand in front of it. That
 * bundle is *the same one the web is served* — so the two facts the packaged build used to
 * bake in have to arrive some other way. The bootstrap sets them before the module loads.
 *
 * ## Why it cannot be turned on by a deployment
 *
 * Only from `file:`. That is not a proxy for "is this the widget" — it is the literal
 * condition the packaged flow exists for: a document with no origin has no cookie jar for
 * a refresh cookie to live in. An https deployment cannot satisfy it no matter how it is
 * configured, which is exactly the failure the block below was written to prevent.
 *
 * This is **stricter** than the build-time flag it joins, not looser: `VITE_PACKAGED=true`
 * typed into a web build's environment would still switch the cookie path off, and no
 * value of this one can.
 */
interface Bootstrap {
  /** Absolute origin of the API, since a relative path from `file:` reaches the disk. */
  apiBase?: string;
  /** Claims the packaged auth flow. Ignored unless the document is genuinely local. */
  packaged?: boolean;
}

function bootstrap(): Bootstrap | null {
  if (typeof window === 'undefined') return null;
  if (window.location.protocol !== 'file:') return null;
  const value = window.__FILMODASI_BOOTSTRAP__;
  return value && typeof value === 'object' ? value : null;
}

const BOOT = bootstrap();

const API_BASE = (BOOT?.apiBase ?? import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export function apiUrl(path: string): string {
  return path.startsWith('/') ? API_BASE + path : path;
}

/**
 * An absolute origin another device can open, or null when there is none.
 *
 * There is exactly one caller — the pairing QR — and it is the only URL in the app that
 * has to survive leaving the machine that built it. Everything else may stay relative.
 *
 * ## The document's own origin wins whenever it has one
 *
 * Including the television's browser tier — a page served over https is reachable at the
 * address it was served from, and that is true however the API is configured. `API_BASE`
 * is consulted only when the document has no usable origin, which is precisely and only
 * the packaged widget: it runs from `file:`, Blink reports its origin as the string
 * `"file://"`, and a QR built from that encodes `file:///pair?code=ABC123` — which a phone
 * camera reads perfectly and then cannot open. That was the bug, and it is the worst shape
 * a failure can take: the television looked right and simply did nothing.
 *
 * Preferring `API_BASE` outright would have fixed the widget and broken a split-origin
 * setup, where it names the API rather than the site. On the widget the two are the same
 * host, so the narrower rule costs nothing.
 *
 * Null rather than a guess when neither is usable: the pairing screen shows the six
 * characters as a first-class path already, so it can drop the QR and lose nothing except
 * the thing that was never going to work.
 */
export function publicOrigin(): string | null {
  return shareableOrigin(typeof window === 'undefined' ? '' : window.location.origin, API_BASE);
}

/**
 * The rule above, as a function of its two inputs.
 *
 * Split out to be testable, which is not the usual justification and is the right one
 * here: the context this exists for is a `file:` document, and no test harness in this
 * project can build one. jsdom refuses to create a window at a `file:` URL at all —
 * `localStorage is not available for opaque origins` — and its `location.origin` cannot
 * be shadowed. So the choice was a pure function with the failing input written down, or
 * no coverage of the branch that broke.
 */
export function shareableOrigin(documentOrigin: string, apiBase: string): string | null {
  if (/^https?:\/\//i.test(documentOrigin)) return documentOrigin;
  return /^https?:\/\//i.test(apiBase) ? apiBase : null;
}

/**
 * Where the app's own static files are, for code that builds a URL to one.
 *
 * `import.meta.env.BASE_URL` is `/` in the deployed build, so a path built from it is
 * root-relative — and from the loader's `file:` document that resolves to the filesystem
 * root, where there are no avatars.
 *
 * ## Why not a `<base href>` in the loader instead
 *
 * That was the plan and it would have broken every navigation. `HashRouter` calls
 * `history.pushState(state, '', '#/home')`, and a relative URL passed to `pushState` is
 * resolved against the **document base URL**. With a base pointing at the server, `#/home`
 * becomes `https://filmodasi.poyland.com/#/home` — a different origin from `file://` — and
 * Blink throws `SecurityError`. That is the same failure `main.tsx` documents for
 * `BrowserRouter`, arriving by a different route, and it would have taken the whole app
 * down on the first navigation after sign-in.
 *
 * So no base element, and the two things that genuinely need an absolute URL say so: API
 * calls through `apiUrl`, static assets through here.
 */
export function assetUrl(path: string): string {
  // `BASE_URL` is `/` on the web, which must stay a root-relative answer; `API_BASE` is an
  // origin with no trailing slash. Normalising both ends is what keeps the join from
  // producing `https://hostavatars/x.svg` or `//avatars/x.svg`.
  const base = API_BASE || import.meta.env.BASE_URL.replace(/\/$/, '');
  return `${base}/${path.replace(/^\//, '')}`;
}

/**
 * Whether this build talks to the API as a packaged Tizen client.
 *
 * What it changes is where the refresh token lives. On the web it is a Secure, httpOnly,
 * SameSite cookie the browser holds and this code cannot read. From `file://` there is no
 * origin for a cookie to belong to, so the server's dedicated packaged-Tizen endpoints
 * return the refresh token in the response body instead — a token stamped `ct: 'tizen'`,
 * which the web cookie path refuses to accept, and vice versa. The two flows cannot be
 * used to escalate into each other.
 *
 * ## Why this is its own flag rather than `API_BASE !== ''`
 *
 * It used to be inferred from having an API base, on the reasoning that a build which had
 * to be told where the API is, is a build that is not being served by it. That holds for
 * the two builds we ship — and it silently conflates two independent facts: *cross-origin*
 * and *no cookie jar*.
 *
 * They come apart the moment anything else sets an API base. Running the dev server
 * against a separate API is the harmless case, and it fails loudly: login is refused with
 * "Tizen authentication is not enabled on this server", which is how this was found.
 *
 * The case that matters is silent. Serve the web app from a different origin than the API
 * — an ordinary thing to do, a CDN or a split deployment — and the *web* build would
 * decide it was a packaged client and start keeping refresh tokens in `localStorage`.
 * That is a binding requirement (normal web clients receive the refresh token only
 * through a Secure, httpOnly, SameSite cookie) being switched off by a deployment
 * decision, with nothing failing to indicate it.
 *
 * So the packaged build says so, explicitly: `web/tizen/build.sh` sets `VITE_PACKAGED`,
 * and nothing else does. A configuration change can no longer imply it.
 *
 * ## The second way in, and why it is safe
 *
 * The loader shape (§29.2) runs the *deployed* bundle inside the widget, and that bundle
 * has no `VITE_PACKAGED` baked into it — it is the same file the web downloads. So the
 * bootstrap can claim it, and `bootstrap()` above honours the claim only from a `file:`
 * document. The reasoning in this comment survives intact: the danger was a deployment
 * *implying* the flag, and an https origin can never satisfy that test.
 */
export const isPackagedClient =
  BOOT?.packaged === true || import.meta.env.VITE_PACKAGED === 'true';

/**
 * The packaged client's refresh token, and the one place it is stored.
 *
 * `localStorage`, deliberately, and only in the packaged build: it has to survive the TV
 * being switched off, and a widget has no third-party script surface for an XSS payload
 * to arrive on. On the web this is never written — the cookie is.
 */
const REFRESH_KEY = 'filmodasi.refreshToken';
const DEVICE_KEY = 'filmodasi.deviceId';

let accessToken: string | null = null;
let profileId: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function storedRefreshToken(): string | null {
  return isPackagedClient ? localStorage.getItem(REFRESH_KEY) : null;
}

export function setRefreshToken(token: string | null): void {
  if (!isPackagedClient) return;
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
}

/**
 * A stable id for this television.
 *
 * The server logs it against the packaged login so a device can be identified later. Not
 * a secret and not a credential — it identifies the box, never the person.
 */
export function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = `tv-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

/** Headers that mark a request as coming from the packaged widget. */
export function packagedHeaders(): Record<string, string> {
  return isPackagedClient ? { 'x-client-type': 'tizen', 'x-tizen-device-id': deviceId() } : {};
}

/** Called when refresh fails, so the app can send the user back to the login screen. */
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * Read-only access to the current token.
 *
 * Exists for the one call that cannot go through `api()`: the final progress report on
 * page hide, which runs as the page is torn down and would not survive a
 * 401-refresh-retry cycle. Deliberately not a setter and deliberately not exported to
 * anything else — the token stays out of storage and out of `window`.
 */
export function currentAccessToken(): string | null {
  return accessToken;
}

export function setProfileId(id: string | null): void {
  profileId = id;
  // Survives a reload; it is not a secret, and the server re-verifies ownership on
  // every request anyway.
  if (id) localStorage.setItem('filmodasi.profileId', id);
  else localStorage.removeItem('filmodasi.profileId');
}

export function storedProfileId(): string | null {
  return localStorage.getItem('filmodasi.profileId');
}

/**
 * PIN grants, keyed by profile id.
 *
 * `sessionStorage`, not `localStorage`, and the difference is the point: a grant unlocks
 * one household member's profile, so it should survive a page reload but not outlive the
 * tab. Leaving it in localStorage would mean the TV browser stays unlocked for whoever
 * turns it on next, which is exactly the situation the PIN exists to prevent.
 */
const GRANT_PREFIX = 'filmodasi.grant.';

export function setProfileGrant(id: string, token: string | null): void {
  if (token) sessionStorage.setItem(GRANT_PREFIX + id, token);
  else sessionStorage.removeItem(GRANT_PREFIX + id);
}

export function storedProfileGrant(id: string): string | null {
  return sessionStorage.getItem(GRANT_PREFIX + id);
}

export function setAuthLostHandler(fn: (() => void) | null): void {
  onAuthLost = fn;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Skips the 401 → refresh → retry cycle. Used by the refresh call itself. */
  noRetry?: boolean;
  /** Omits X-Profile-Id for account-level endpoints. */
  withoutProfile?: boolean;
  signal?: AbortSignal;
}

async function refresh(): Promise<boolean> {
  // Concurrent 401s share one rotation; two parallel refreshes would race and one would
  // invalidate the other's token.
  refreshInFlight ??= (async () => {
    try {
      const stored = storedRefreshToken();
      const res = await fetch(apiUrl('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include', // on the web the refresh token is the httpOnly cookie
        headers: { 'content-type': 'application/json', ...packagedHeaders() },
        // The packaged client has no cookie to send, so it sends the token it was given.
        // The server accepts a body token only if that token was minted as `tizen`.
        body: JSON.stringify(stored ? { refreshToken: stored } : {}),
      });
      if (!res.ok) {
        // A refused refresh is terminal: the stored token is spent, revoked, or from
        // another account. Keeping it would mean retrying it forever.
        if (res.status === 401 || res.status === 403) setRefreshToken(null);
        return false;
      }
      const data = (await res.json()) as { accessToken: string; refreshToken?: string };
      accessToken = data.accessToken;
      // Rotation: the server issues a new refresh token each time, and the old one is
      // dead the moment this one arrives.
      if (data.refreshToken) setRefreshToken(data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { ...packagedHeaders() };
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (profileId && !options.withoutProfile) {
      headers['x-profile-id'] = profileId;
      // Only locked profiles have one; the server ignores it otherwise.
      const grant = storedProfileGrant(profileId);
      if (grant) headers['x-profile-token'] = grant;
    }

    return fetch(apiUrl(path), {
      method: options.method ?? 'GET',
      credentials: 'include',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  };

  let res: Response;
  try {
    res = await send();
  } catch (err) {
    if (options.signal?.aborted) throw err;
    throw new ApiRequestError(0, 'NETWORK', 'Sunucuya ulaşılamadı');
  }

  if (res.status === 401 && !options.noRetry) {
    if (await refresh()) {
      res = await send();
    } else {
      onAuthLost?.();
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = parsed as ApiError | null;
    throw new ApiRequestError(
      res.status,
      err?.code ?? 'INTERNAL',
      err?.message ?? `İstek başarısız (${res.status})`,
      err?.details,
    );
  }

  return parsed as T;
}

/**
 * Whether an access token is held in memory right now.
 *
 * "In memory" is the whole meaning: it says nothing about whether the httpOnly refresh
 * cookie is still valid, so `false` here does not mean signed out — it is also what a
 * fresh tab reports before the first refresh has run. Callers deciding what to render
 * want the session store's loading state, not this.
 *
 * (This carried a comment describing a function that added the access token to a URL as
 * a query string. No such function exists — tokens are never put in a URI, which is the
 * premise the whole log-redaction layer rests on — and a comment claiming otherwise is
 * an invitation to write one.)
 */
export function hasSession(): boolean {
  return accessToken !== null;
}
